import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Alert, Box, Chip, Link } from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';

import type {
  AutoAssignRankingQuery,
  CurrentSubscription,
} from '@graphql/__generated__/graphql';
import { useLocale } from '@shared/hooks/locale';
import { formatPrice } from '@shared/utils/format-price';

const AUTO_ASSIGN_RANKING_QUERY = gql`
  query AutoAssignRanking($lead_id: uuid!) {
    auto_assign_ranking(lead_id: $lead_id) {
      message
      should_auto_assign
      assign_to {
        id
        full_name
        crm_link
      }
      agents {
        id
        full_name
        crm_link
        auto_assign_leads
        cost_per_lead
        amount_spent
        assigned_leads
        already_assigned
        subscriptions {
          id
          status
        }
      }
    }
  }
`;

interface Props {
  leadId: string;
}

const RenderStatus = ({ status }: { status?: string | null }) => {
  switch (status) {
    case 'active':
      return <Chip color="success" label={status} size="small" />;
    case 'past_due':
      return (
        <Chip color="warning" label={status} variant="outlined" size="small" />
      );
    case 'trialing':
    case 'incomplete_expired':
    case 'canceled':
    default:
      return (
        <Chip
          color="primary"
          label={status ?? '—'}
          variant="outlined"
          size="small"
        />
      );
  }
};

const RenderAutoAssign = ({
  auto_assign_leads,
}: {
  auto_assign_leads?: boolean | null;
}) => {
  if (auto_assign_leads === true) {
    return <Chip color="success" label="Yes" size="small" />;
  } else if (auto_assign_leads === false) {
    return <Chip color="error" label="No" variant="outlined" size="small" />;
  }
  return (
    <Chip color="primary" label="Default" variant="outlined" size="small" />
  );
};

export const AutoAssignAssistantEU = ({ leadId: lead_id }: Props) => {
  const { locale } = useLocale();
  const { data, loading, error } = useQuery<AutoAssignRankingQuery>(
    AUTO_ASSIGN_RANKING_QUERY,
    {
      variables: { lead_id },
      fetchPolicy: 'cache-and-network',
    },
  );

  const ranking = data?.auto_assign_ranking;

  const rows = useMemo(
    () =>
      (ranking?.agents ?? []).filter(
        agent => agent?.id != null && agent?.full_name != null,
      ),
    [ranking?.agents],
  );

  const columns: GridColDef[] = [
    {
      field: 'full_name',
      headerName: 'Agent',
      minWidth: 160,
      flex: 2,
      renderCell: ({ value, row }) => (
        <Link href={`${row.crm_link}`} target="_blank">
          {value}
        </Link>
      ),
    },
    {
      field: 'subscriptions',
      headerName: 'Subscriptions',
      flex: 3,
      minWidth: 220,
      renderCell: ({ row }) => {
        const subscriptions = row.subscriptions ?? [];
        if (subscriptions.length === 0) {
          return <span>—</span>;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              py: 1,
              width: '100%',
            }}
          >
            {subscriptions.map(
              (subscription: CurrentSubscription, index: number) => (
                <RenderStatus
                  key={subscription?.id ?? `${row.id}-sub-${index}`}
                  status={subscription?.status}
                />
              ),
            )}
          </Box>
        );
      },
    },
    {
      field: 'auto_assign_leads',
      headerName: 'Assign',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) => <RenderAutoAssign auto_assign_leads={value} />,
    },
    {
      field: 'already_assigned',
      headerName: 'Received',
      flex: 1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) =>
        value ? (
          <Chip color="error" label="Yes" size="small" />
        ) : (
          <Chip color="primary" label="No" variant="outlined" size="small" />
        ),
    },
    {
      field: 'amount_spent',
      headerName: 'Spend',
      flex: 1,
      minWidth: 170,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) => formatPrice(value, locale, 'EUR', 0, '—'),
    },
    {
      field: 'assigned_leads',
      headerName: 'Leads',
      flex: 1,
      minWidth: 170,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => {
        const numValue = value as number | null | undefined;
        return numValue != null ? Math.round(numValue).toString() : '—';
      },
    },
    {
      field: 'cost_per_lead',
      headerName: 'CPL',
      flex: 1,
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) => formatPrice(value, locale, 'EUR', 0, '—'),
    },
  ];

  if (error) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <DataGridPremium
      loading={loading}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      rows={rows}
      columns={columns}
      getRowHeight={() => 'auto'}
      hideFooter
      hideFooterPagination
      disableColumnMenu
      sx={{
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
      }}
    />
  );
};
